import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import LogoImage from '../../assets/images/wwm-logo-2.png'; // Base64 or hosted image URL recommended

const styles = StyleSheet.create({
  page: {
    paddingTop: 10,
    paddingHorizontal: 20,
    paddingBottom: 20,
    fontSize: 6,
    flexDirection: 'column',
  },
  infoSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    border: '1pt solid #ccc',
    borderLeft: '4pt solid #223A64',
    padding: 8,
    borderRadius: 6,
    marginBottom: 10,
  },
  infoLeft: {
    flex: 0.55,
  },
  infoRight: {
    flex: 0.45,
    alignItems: 'flex-end',
  },
  logo: {
    width: 70,
    marginBottom: 10, 
  },
  row: {
    flexDirection: 'row',
    marginBottom: 13,
    flexWrap: 'wrap',
  },
  label: {
    fontWeight: 'bold',
    marginRight: 4,
    minWidth: 65,
  },
  chartImage: {
    width: 140,
    height: 140,
    marginTop: 4,
  },
  tableContainer: {
    flexDirection: 'column',
    border: '1pt solid #ccc',          // Full outer border
    borderRadius: 4,                   // Optional: rounded corners
    overflow: 'hidden',               // Optional: ensures clean edges
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#223A64',
    color: 'white',
    fontWeight: 'bold',
    padding: 4,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '0.5pt solid #ccc',
    paddingVertical: 4,
  },
  tableRowBudget: {
    flexDirection: 'row',
    borderBottom: '0.5pt solid #ccc',
    paddingVertical: 3,
  },
  subtotalRow: {
    flexDirection: 'row',
    backgroundColor: '#e6f0ff',
    fontWeight: 'bold',
    paddingVertical: 2,
  },
  grandTotal: {
    flexDirection: 'row',
    backgroundColor: '#cce0ff',
    fontWeight: 'bold',
    paddingVertical: 3,
  },
  tableCellCode: {
    width: '28%',
    paddingHorizontal: 2,
  },
  tableCellDescription: {
    width: '44%',
    paddingHorizontal: 2,
  },
  tableCellAmount: {
    width: '28%',
    paddingHorizontal: 2,
    textAlign: 'left',
  },
  tableBold: {
    fontWeight: 'bold',
  },
  tableTitle: {
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 6,  },
  
    metricsHeaderRow: {
      backgroundColor: '#223A64',
      paddingVertical: 5,
      alignItems: 'center', // 👈 this centers children horizontally
    },
    
    metricsTitle: {
      color: 'white',
      fontWeight: 'bold',
      fontSize: 8,
    },
  
});

const PdfExportDocument = ({ formData, chartImage }) => {
  const info = formData?.projectInfo ?? {};

  const renderRowBudget = (code, description, amount) => (
    <View style={styles.tableRowBudget}>
      <Text style={styles.tableCellCode}>{code}</Text>
      <Text style={styles.tableCellDescription}>{description}</Text>
      <Text style={styles.tableCellAmount}>{amount}</Text>
    </View>
  );

  const renderRow = (code, description, amount) => (
    <View style={styles.tableRow}>
      <Text style={styles.tableCellCode}>{code}</Text>
      <Text style={styles.tableCellDescription}>{description}</Text>
      <Text style={styles.tableCellAmount}>{amount}</Text>
    </View>
  );

  const renderSubtotal = (label, amount) => (
    <View style={styles.subtotalRow}>
      <Text style={styles.tableCellCode}>{label}</Text>
      <Text style={styles.tableCellDescription}></Text>
      <Text style={styles.tableCellAmount}>{amount}</Text>
    </View>
  );

  const renderTotal = (label, amount) => (
    <View style={styles.grandTotal}>
      <Text style={styles.tableCellCode}>{label}</Text>
      <Text style={styles.tableCellDescription}></Text>
      <Text style={styles.tableCellAmount}>{amount}</Text>
    </View>
  );

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page} wrap={false}>
        {/* Header Section */}
        <View style={{ flexDirection: 'row', marginBottom: 10 }}>
        
        {/* LEFT: Project Info + Chart */}
        <View style={{ width: '50%' }}>
          <View style={styles.infoSection}>
            <View style={styles.infoLeft}>
              <Image src={LogoImage} style={styles.logo} />
              <View style={styles.row}><Text style={styles.label}>Project Name:</Text><Text>{info.projectName || '—'}</Text></View>
              <View style={styles.row}><Text style={styles.label}>Project Scope:</Text><Text>{info.projectScope || '—'}</Text></View>
              <View style={styles.row}><Text style={styles.label}>Date:</Text><Text>{info.date ? new Date(info.date).toLocaleDateString() : '—'}</Text></View>
              <View style={styles.row}><Text style={styles.label}>Estimated By:</Text><Text>{info.estimatedBy || '—'}</Text></View>
              <View style={styles.row}><Text style={styles.label}>Requested By:</Text><Text>{info.requestedBy || '—'}</Text></View>
              <View style={styles.row}><Text style={styles.label}>Estimate Class:</Text><Text>{info.estimateClass || '—'}</Text></View>
            </View>

            <View style={{ marginTop: 6, alignItems: 'center' }}>
              {chartImage ? (
                <Image style={styles.chartImage} src={chartImage} />
              ) : (
                <Text>No chart image available.</Text>
              )}
            </View>
          </View>
        </View>

        {/* RIGHT: Small Summary Table */}
        <View style={{ width: '50%', paddingLeft: 10 }}>

        <View style={styles.tableContainer}>
  <View style={styles.metricsHeaderRow}>
    <Text style={styles.metricsTitle}>Project Quantities</Text>
  </View>

  {renderRow('', 'Foreign Line Crossings', '7 EA')}
  {renderRow('', 'Construction Mats', '600 EA')}
  {renderRow('', 'Number of Bores', '2 EA')}
  {renderRow('', 'Horizontal Directional Drill', '0 LF')}
  {renderRow('', 'Wetlands & Waterbodies', '750 LF')}
  {renderRow('', 'ROW $/Rod', '$1,860')}
  {renderRow('', 'Base Lay $/ft', '$87')}
  {renderRow('', 'Total Tonnage', '145')}
  {renderRow('', '$ /ton', '$1,396')}
  {renderRow('', 'Construction $/ft', '$87')}
  {renderRow('', 'Base $/in-mile', '$211,706')}
</View>


</View>
</View>



        {/* Content Section */}
        <View style={{ flexDirection: 'row', marginTop: 10 }}>
          {/* Left Half Table */}
          <View style={{ width: '50%', marginRight: 10 }}>
            <View style={styles.tableContainer}>
              {/* Table Header */}
              <View style={styles.tableHeader}>
                <Text style={[styles.tableCellCode, styles.tableBold]}>Code</Text>
                <Text style={[styles.tableCellDescription, styles.tableBold]}>Description</Text>
                <Text style={[styles.tableCellAmount, styles.tableBold]}>Cost Estimate</Text>
              </View>

              {/* Example Rows */}
              {renderRowBudget('141-01-1001', 'Land Owner Payments', '$1,452,700.00')}
              {renderRowBudget('141-01-1008', 'Right of Way Agent Fees', '$188,500.00')}
              {renderRowBudget('141-01-1004', 'Survey', '$78,478.00')}
              {renderRowBudget('141-01-1007', 'Miscellaneous Fees & Permits', '$0.00')}
              {renderSubtotal('ROW Subtotal', '$1,719,678.00')}

              {renderRowBudget('141-01-2001', 'Line Pipe and Bore Pipe', '$202,105.00')}
              {renderRowBudget('141-01-2002', 'Pipe Valves and Fittings', '$18,672.00')}
              {renderRowBudget('141-01-2022', 'Cathodic Protection', '$13,200.00')}
              {renderRowBudget('141-01-2023', 'Materials Miscellaneous', '$7,946.00')}
              {renderRowBudget('141-01-2041', 'Initial Fill', '$1,155.00')}
              {renderRowBudget('141-01-2052', 'Freight', '$13,484.00')}
              {renderSubtotal('Materials Subtotal', '$256,562.00')}

              {renderRowBudget('141-01-3008', 'Construction Labor', '$1,272,325.00')}
              {renderRowBudget('141-01-3013', 'Non-Destructive Examination', '$25,128.00')}
              {renderRowBudget('141-01-4006', 'Engineering Services', '$191,901.00')}
              {renderRowBudget('141-01-4007', 'EPC Contractors', '$0.00')}
              {renderSubtotal('Construction Subtotal', '$1,489,353.00')}

              {renderRowBudget('141-01-4001', 'Inspection Services', '$144,435.00')}
              {renderRowBudget('141-01-4002', 'Capitalized Salaries & Wages', '$0.00')}
              {renderRowBudget('141-01-4008', 'Capitalized Travel', '$5,000.00')}
              {renderRowBudget('141-01-4004', 'Environmental Services', '$56,784.00')}
              {renderRowBudget('141-01-5001', '3rd Party Interconnect Fees', '$0.00')}
              {renderRowBudget('141-01-6002', 'Legal Fees Services', '$120,000.00')}
              {renderRowBudget('141-01-6009', 'Project Overhead (Indirect)', '$75,836.00')}
              {renderSubtotal('Project Management Subtotal', '$402,055.00')}

              {renderRowBudget('', 'Project Base Cost', '$3,867,650.00')}
              {renderRowBudget('', 'Contingency', '$376,849.00')}
              {renderTotal('Project Budget', '$4,244,499.00')}
            </View>
          </View>

          {/* Right Half Placeholder */}
          <View style={{ flex: 1 }}>
          <View style={{ width: '100%', marginRight: 10 }}>
          <View style={styles.tableContainer}>
  {/* Title Row - Centered */}
  <View style={styles.metricsHeaderRow}>
    <Text style={styles.metricsTitle}>Project Metrics</Text>
  </View>

  {/* Data Rows */}
  {renderRow('', 'Project Miles', '2.76')}
  {renderRow('', 'Project Diameter', '6.625')}
  {renderRow('', '$ /in-mile', '$241,072.21')}
  {renderRow('', 'Contingency %', '10%')}
  {renderRow('', 'Total Contingency', '$363,997.15')}
  {renderRow('', 'Total Tax', '$20,891.97')}
</View>

<View style={[styles.tableContainer, { marginTop: 20 }]}>
  <View style={styles.metricsHeaderRow}>
    <Text style={styles.metricsTitle}>Cost Assumptions</Text>
  </View>

  {renderRow('','$/ft Pipe Rolled & Coated', '$12.35')}
  {renderRow('','$/ft construction base lay', '$88.65')}
  {renderRow('','$/ft row', '$112.71')}
  {renderRow('','Pipe Coating Price', '-')}
  {renderRow('','$/ST Pipe Bare', '$1,272.00')}
  {renderRow('','$/ST Pipe Coated', '$1,241.24')}
  {renderRow('','$/ST Pipe Delivered', '$1,241.24')}
</View>

          </View>     
         </View>
        </View>
      </Page>
    </Document>
  );
};

export default PdfExportDocument;
